/**
 * Phase 45 Plan 45-08 (D-16) — TaskDetailSlideOver attachments section.
 *
 * Task-level attachments (separate from per-comment attachments per D-16).
 * Upload uses multipart server-side flow per the 45-04 SUMMARY Deviation
 * #1 (fileUploadService has no presigned-PUT helper).
 *
 * The 25 MB / MIME allowlist / 10-per-task caps are enforced on the backend;
 * the FE surfaces the resulting locked error codes via useTaskAttachments
 * toast branches.
 *
 * Quick task 260810-lpq — LPQ-01..04:
 *   - Every row exposes a separate explicit download control that mints a
 *     save-forcing signed URL independent of the view/click action.
 *   - No link target here is built from the list-response `downloadUrl`
 *     field (which can go stale past the server's 1-hour TTL if a slide-over
 *     is left open) — every open/download uses a URL minted at click time
 *     (or a <50-minute-old cache) via `useTaskAttachmentDownloadUrl`.
 *   - Image rows render a thumbnail (via the shared `ImagePreviewThumb`)
 *     with a hover preview.
 *
 * MKS-274 — clicking an image row (its thumbnail or its filename) opens the
 * attachment full size in an `ImageLightbox` overlay instead of minting a URL
 * and opening a browser tab. The explicit download control and every
 * non-image row are unchanged.
 */
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { ArrowDownTrayIcon, PaperClipIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  useTaskAttachments,
  type TaskAttachmentDto,
} from '../../hooks/useTaskAttachments';
import { useTaskAttachmentDownloadUrl } from '../../hooks/useTaskAttachmentDownloadUrl';
import { ImagePreviewThumb } from '../common/ImagePreviewThumb';
import { ImageLightbox } from '../common/ImageLightbox';

interface TaskAttachmentsSectionProps {
  taskId: number;
}

const IMAGE_MIMES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uploaderLabel(att: TaskAttachmentDto): string {
  if (!att.uploader) return '—';
  const parts = [att.uploader.firstName, att.uploader.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '—';
}

export function TaskAttachmentsSection({ taskId }: TaskAttachmentsSectionProps) {
  const { data, isLoading, uploadAttachment, deleteAttachment } =
    useTaskAttachments(taskId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadAttachment.mutate(file, {
      onSettled: () => {
        // Allow re-uploading the same file (input keeps the same value
        // otherwise so onChange wouldn't fire again).
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  }

  function handleDelete(att: TaskAttachmentDto) {
    const ok = window.confirm(`Delete "${att.filename}"?`);
    if (ok) deleteAttachment.mutate(att.id);
  }

  return (
    <section
      aria-label="Attachments"
      className="pt-3 border-t border-ms-border-subtle"
      data-testid="attachments-section"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ms-text-secondary">
          Attachments
        </h3>
        {data.length > 0 && (
          <span className="text-xs text-ms-text-secondary tabular-nums">
            {data.length}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="attachments-file-input"
          aria-label="Choose file to attach"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadAttachment.isPending}
          data-testid="attachments-upload-btn"
          className="inline-flex items-center gap-1 rounded-lg border border-ms-border bg-ms-surface px-3 py-1.5 text-sm font-medium text-ms-text hover:bg-ms-canvas disabled:opacity-50"
        >
          <PaperClipIcon className="w-4 h-4" />
          {uploadAttachment.isPending ? 'Uploading…' : 'Attach file'}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-ms-text-muted italic">Loading attachments…</p>
      ) : data.length === 0 ? (
        <p className="text-sm text-ms-text-muted italic">No attachments yet.</p>
      ) : (
        <ul className="space-y-1" data-testid="attachments-list">
          {data.map((att) => (
            <TaskAttachmentItem
              key={att.id}
              taskId={taskId}
              attachment={att}
              onDelete={() => handleDelete(att)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function TaskAttachmentItem({
  taskId,
  attachment,
  onDelete,
}: {
  taskId: number;
  attachment: TaskAttachmentDto;
  onDelete: () => void;
}) {
  const isImage = IMAGE_MIMES.has(attachment.contentType);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // View mint: images mint eagerly for a fresh thumbnail; non-images mint
  // lazily on click of the filename button. LPQ-02 — never link off the
  // list-response `downloadUrl` field.
  const [viewEnabled, setViewEnabled] = useState(false);
  const enabled = isImage || viewEnabled;
  const { data: viewData } = useTaskAttachmentDownloadUrl(taskId, attachment.id, enabled);

  useEffect(() => {
    if (!isImage && viewEnabled && viewData?.url) {
      window.open(viewData.url, '_blank', 'noopener,noreferrer');
      setViewEnabled(false);
    }
  }, [isImage, viewEnabled, viewData?.url]);

  // Explicit download control (LPQ-01) — a SECOND, independent mint
  // requesting the save-forcing disposition.
  const [downloadEnabled, setDownloadEnabled] = useState(false);
  const { data: downloadData } = useTaskAttachmentDownloadUrl(
    taskId,
    attachment.id,
    downloadEnabled,
    'attachment',
  );
  useEffect(() => {
    if (downloadEnabled && downloadData?.url) {
      window.open(downloadData.url, '_blank', 'noopener,noreferrer');
      setDownloadEnabled(false);
    }
  }, [downloadEnabled, downloadData?.url]);

  // MKS-274 — an image opens in the lightbox overlay; the signed URL is
  // already minted eagerly for the thumbnail.
  function openLightbox() {
    if (viewData?.url) setLightboxOpen(true);
  }

  return (
    <li
      className="flex items-center gap-2 group rounded-md hover:bg-ms-canvas px-2 py-1"
      data-testid={`attachment-row-${attachment.id}`}
    >
      {isImage ? (
        <ImagePreviewThumb
          src={viewData?.url}
          alt={attachment.filename}
          testId={`attachment-thumb-${attachment.id}`}
          onOpen={isImage ? openLightbox : undefined}
        />
      ) : (
        <PaperClipIcon className="w-4 h-4 text-ms-text-muted shrink-0" />
      )}
      <button
        type="button"
        onClick={() => (isImage ? openLightbox() : setViewEnabled(true))}
        className="text-sm text-ms-text hover:underline truncate flex-1 text-left"
        data-testid={`attachment-link-${attachment.id}`}
      >
        {attachment.filename}
      </button>
      {isImage && lightboxOpen && viewData?.url && (
        <ImageLightbox
          src={viewData.url}
          alt={attachment.filename}
          onClose={() => setLightboxOpen(false)}
          testId={`attachment-lightbox-${attachment.id}`}
        />
      )}
      <span className="text-xs text-ms-text-secondary tabular-nums">
        {formatBytes(attachment.sizeBytes)}
      </span>
      <span className="text-xs text-ms-text-secondary truncate max-w-[8rem]">
        {uploaderLabel(attachment)}
      </span>
      <button
        type="button"
        onClick={() => setDownloadEnabled(true)}
        aria-label={`Download ${attachment.filename}`}
        data-testid={`attachment-download-${attachment.id}`}
        className="h-6 w-6 flex items-center justify-center text-ms-text-muted hover:text-ms-text rounded-md hover:bg-ms-canvas"
      >
        <ArrowDownTrayIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onDelete}
        aria-label={`Delete ${attachment.filename}`}
        data-testid={`attachment-delete-${attachment.id}`}
        className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 flex items-center justify-center text-ms-text-muted hover:text-rose-600 rounded-md hover:bg-rose-50"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </li>
  );
}
